import React, { useMemo } from 'react';
import { Spacer } from '@ontruck/orc';
import { Trans, useTranslation } from 'react-i18next';
import Link from 'components/styled/link';
import LoadSpinner from 'components/LoadSpinner';
import Header from './components/Header';
import Actions from './components/Actions';
import Emissions from './components/Emissions';
import MRAdditionalOptions from './components/MRAdditionalOptions';
import Footer from './components/Footer';
import DriverMap from 'components/DriverMap';
import OrderDetailPrice from './components/OrderDetailPrice';
import { useOrder } from 'hooks/useOrders';
import routes from 'utils/routes';
import { history } from 'state/store';
import { useSelector } from 'react-redux';
import { selectors as settingsSelectors } from 'state/settings';
import cx from 'classnames';
import { DONE_STATUSES, ORDER_STATUSES } from 'utils/order/constants';
import ModalSummary from 'components/orders/OrderDetail/components/ModalSummary';
import { useFeasibility } from 'hooks/useFeasibility';
import { isStatusBeforeAssigned } from 'utils/order/methods';
import { IS_TENANT } from 'tenant/utils';
import CustomReferencesTable from 'refactored/features/order/references/CustomReferencesTable';

const SingleOrderDetail = ({ className, match }) => {
  const { orderId } = match?.params;
  const { isLoading, order, error } = useOrder(orderId);
  const { t } = useTranslation('ModalOrderDetail');
  const { getIsOrderNonCore } = useFeasibility();
  const hidePrice = useMemo(
    () => getIsOrderNonCore(order) && isStatusBeforeAssigned(order.status),
    [getIsOrderNonCore, order]
  );
  const opsMail = useSelector(settingsSelectors.getOpsMail);

  const { isEditable, isCancelable, showMap, isDone } = useMemo(
    () => ({
      isEditable: order?.is_editable,
      isCancelable: order?.is_cancelable,
      showMap: order?.status === ORDER_STATUSES.IN_TRANSIT,
      isDone: DONE_STATUSES.includes(order?.status),
      internalReference: order?.shipments?.[0]?.internal_reference,
      comments: order?.stops?.[0]?.comments,
      appointmentInstructions: order?.shipments?.[0]?.appointment_instructions,
      shipmentId: order?.shipments?.[0]?.id,
    }),
    [order]
  );

  if (error) {
    history.push(`${routes.orders.active}/`);
    return null;
  }

  if (isLoading) {
    return (
      <div data-testid="loader">
        <Spacer pTop="L" pBottom="L">
          <LoadSpinner />
        </Spacer>
      </div>
    );
  }

  if (!isLoading && !order) {
    return null;
  }

  return (
    <div
      data-testid="single-order-detail"
      className={cx(className, {
        'fixed-height': showMap,
        'flexible-height': !showMap,
      })}
    >
      <div className="details-column">
        <div className="details-wrapper">
          <Header orderId={orderId} />
          <Actions orderId={orderId} />
          {IS_TENANT && <CustomReferencesTable data={order} />}

          {!IS_TENANT && (
            <>
              {!isDone && (!isCancelable || !isEditable) && (
                <div className="cancel">
                  <p className="disable-link link">
                    <Trans
                      t={t}
                      i18nKey={`Orders:${
                        !isEditable ? 'edit' : 'cancel'
                      }.action${
                        !isCancelable && !isEditable ? 's' : ''
                      }_disable`}
                      values={{ mail: opsMail }}
                      components={[
                        <Link
                          href={`mailto:${opsMail}`}
                          text={opsMail}
                          noTextDecoration
                          target="_blank"
                        />,
                      ]}
                    />
                  </p>
                </div>
              )}
              <div>
                <Emissions show={isDone} emissionsData={order.co2_savings} />
              </div>
            </>
          )}

          <ModalSummary orderId={orderId} />
          <MRAdditionalOptions order={order} />
          {!hidePrice && <OrderDetailPrice order={order} />}
          <Footer orderId={orderId} />
        </div>
      </div>
      <div className="map-column">
        {showMap && <DriverMap stops={order.stops} drivers={order.drivers} />}
      </div>
    </div>
  );
};

export default SingleOrderDetail;
